/// <amd-module name="DistributedTask/Common/AzureResourcesService/AzureResourcesService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { newGuid } from "VSS/Core/Util/String";
import { IProjectData } from "Tfs/Platform/Page";
import { getServiceEndpointClient, IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";
import { AzureEndpointConstants } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { AadAuthorizer } from "DistributedTask/Common/AadAuthorizer/AadAuthorizer";
import {
    ServiceEndpoint,
    AccessTokenRequestType,
    ServiceEndpointRequest,
    AzureSubscriptionQueryResult,
    AzureManagementGroupQueryResult,
    ServiceEndpointDetails,
    AzureSubscription,
    ServiceEndpointRequestResult,
    EndpointAuthorization,
    AzureMLWorkspace,
    AzureAppService
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IKubernetesCluster, IAzureRequestResult } from "DistributedTask/Common/AzureResourcesService/AzureResourceContracts";
import { getProject, safeParseJson, getResourceGroupFromClusterId } from "DistributedTask/Common/AzureResourcesService/Utils";
import * as Constants from "DistributedTask/Common/AzureResourcesService/Constants";

export interface IAzureResourcesService extends IVssService {
    getAzureSubscriptions(): Promise<AzureSubscriptionQueryResult>;

    getManagementGroups(): Promise<AzureManagementGroupQueryResult>;

    getAADTenantId(): Promise<string>;

    getAccessToken(azureSubscriptionTenantId: string, aadTenantId?: string, accessToken?: any, endpointId?: any): Promise<string | undefined>;

    getAzureContainerRegistries(
        azureSubscription: AzureSubscription,
        accessToken: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<any>;

    getAzureKubernetesClusters(
        azureSubscription: AzureSubscription,
        accessToken: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<IAzureRequestResult>;

    getAzureKubernetesNamespaces(
        azureSubscription: AzureSubscription,
        accessToken: string,
        clusterId: string,
        clusterFqdn: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<IAzureRequestResult>;

    getAzureRMResourceGroups(
        azureSubscription: AzureSubscription,
        accessToken: string,
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<IAzureRequestResult>;

    getAzureMLWorkspaces(
        azureSubscription: AzureSubscription,
        accessToken: string,
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        resourceGroupName: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<IAzureRequestResult>;

    getAzureAppServiceAndSlots(
        azureSubscription: AzureSubscription,
        accessToken: string,
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        resourceGroupName: string,
        callbackInvocation: boolean,
        callbackParameters: { [key: string]: string }): Promise<IAzureRequestResult>;
}

export class AzureResourcesService extends VssService implements IAzureResourcesService {
    public getAzureSubscriptions(): Promise<AzureSubscriptionQueryResult> {
        return this._getServiceEndpointsClient().getAzureSubscriptions();
    }

    public getManagementGroups(): Promise<AzureManagementGroupQueryResult> {
        return this._getServiceEndpointsClient().getAzureManagementGroups();
    }

    public getAADTenantId(): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!!this._aadTenantId) {
                resolve(this._aadTenantId);
            }
            else {
                this._getTaskAgentClient().getVstsAadTenantId().then((tenantId) => {
                    this._aadTenantId = tenantId;
                    resolve(tenantId);
                }, (error: any) => {
                    reject(error);
                });
            }
        });
    }

    public getAccessToken(azureSubscriptionTenantId: string, accessToken?: any, endpointId?: any): Promise<string | undefined> {
        if (!this._accessTokenCachePromise[azureSubscriptionTenantId]) {
            this._accessTokenCachePromise[azureSubscriptionTenantId] = new Promise((resolve, reject) => {
                this.getAADTenantId().then((aadTenantId) => {
                    if (azureSubscriptionTenantId && aadTenantId && azureSubscriptionTenantId !== aadTenantId) {
                        const projectData: IProjectData | undefined = getProject(this.pageContext);
                        const aadAuthorizer = new AadAuthorizer(this.pageContext);
                        aadAuthorizer.getAccessTokenKey(projectData, azureSubscriptionTenantId, aadTenantId || "", accessToken, endpointId).then((accessTokenKey: string) => {
                            resolve(accessTokenKey);
                        }, (error: any) => {
                            reject(error);
                        });
                    }
                    else {
                        resolve(undefined);
                    }
                });
            });
        }

        return this._accessTokenCachePromise[azureSubscriptionTenantId];
    }

    public getAzureContainerRegistries(
        azureSubscription: AzureSubscription,
        accessToken: string,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<any> {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForAzureRM(AzureEndpointConstants.SpnKeyAuthType, AzureEndpointConstants.SpnCreationModeAutomatic, AzureEndpointConstants.SubscriptionString, undefined, azureSubscription, undefined);
        let registriesList: any[] = [];

        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureContainerRegistries,
                serviceEndpointDetails,
                "",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                result.data.forEach((registry: any) => {
                    registriesList.push(JSON.parse(registry));
                });

                resolve(registriesList);
            });
        });
    }

    public getAzureKubernetesClusters(
        azureSubscription: AzureSubscription,
        accessToken: string,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<IAzureRequestResult> {
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureKubernetesClusters,
                undefined,
                "",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                let parsedResult: IAzureRequestResult = {
                    data: this._parseClusterResultJson(result.data),
                    callbackParameters: result.callbackParameters,
                    callbackRequired: result.callbackRequired
                };
                resolve(parsedResult);
            });
        });
    }

    public getAzureKubernetesNamespaces(
        azureSubscription: AzureSubscription,
        accessToken: string,
        clusterId: string,
        clusterFqdn: string,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<IAzureRequestResult> {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForNamespaces(clusterId, clusterFqdn, azureSubscription, undefined);
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.KubernetesNamespaces,
                serviceEndpointDetails,
                "",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                let parsedResult: IAzureRequestResult = {
                    data: this._parseNamespacesResultJson(result.data),
                    callbackParameters: result.callbackParameters,
                    callbackRequired: result.callbackRequired
                };
                resolve(parsedResult);
            });
        });
    }

    public getAzureRMResourceGroups(
        azureSubscription: AzureSubscription,
        accessToken: string,
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<IAzureRequestResult> {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForAzureRM(authenticationType, creationMode, scopeLevel, undefined, azureSubscription, undefined);
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureResourceGroups,
                serviceEndpointDetails,
                "",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                let parsedResult: IAzureRequestResult = {
                    data: result.data,
                    callbackParameters: result.callbackParameters,
                    callbackRequired: result.callbackRequired
                };
                resolve(parsedResult);
            });
        });
    }

    public getAzureMLWorkspaces(
        azureSubscription: AzureSubscription,
        accessToken: string,
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        resourceGroupName: string,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<IAzureRequestResult> {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForAzureRM(authenticationType, creationMode, scopeLevel, resourceGroupName, azureSubscription, undefined);
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureMachineLearningWorkspaces,
                serviceEndpointDetails,
                "{ \"Name\" : \"{{name}}\", \"Location\" : \"{{location}}\", \"Id\" : \"{{properties.workspaceId}}\" }",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                let parsedResult: IAzureRequestResult = {
                    data: this._parseMLWorkspacesResultJson(result.data),
                    callbackParameters: result.callbackParameters,
                    callbackRequired: result.callbackRequired
                };
                resolve(parsedResult);
            });
        });
    }

    public getAzureAppServiceAndSlots(azureSubscription: AzureSubscription, accessToken: string, authenticationType: string, 
        creationMode: string, scopeLevel: string, resourceGroupName: string, callbackInvocation: boolean,
         callbackParameters: { [key: string]: string; }): Promise<IAzureRequestResult> {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForAzureRM(authenticationType, creationMode, scopeLevel, resourceGroupName, azureSubscription, undefined);
        let appServiceResult: IAzureRequestResult; 
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureRMWebAppByResourceGroup,
                serviceEndpointDetails,
                "{ \"Name\" : \"{{name}}\", \"Id\" : \"{{id}}\" }",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                appServiceResult = {
                    data: this._parseAppServiceResultJson(result.data),
                    callbackParameters: result.callbackParameters,
                    callbackRequired: result.callbackRequired
                };

                let slotPromiseArray: Promise<AzureAppService[]>[] = [];
                if (appServiceResult.data) {
                    let appServiceList = appServiceResult.data as AzureAppService[];
                    appServiceList.forEach((appService: AzureAppService) => {
                        slotPromiseArray.push(this._getAzureAppServiceSlots(azureSubscription, accessToken, authenticationType, creationMode, scopeLevel, resourceGroupName, callbackInvocation, callbackParameters, appService.name));
                    });
                }
                return Promise.all(slotPromiseArray);
            }).then((slotPromiseResultArray: AzureAppService[][]) => {
                let slots: AzureAppService[] = slotPromiseResultArray.reduce((acc, val) => acc.concat(val), []);
                appServiceResult.data = (appServiceResult.data as AzureAppService[]).concat(slots);
                resolve(appServiceResult);
            });
        });
    }

    public _getAzureAppServiceSlots(azureSubscription: AzureSubscription, accessToken: string, authenticationType: string, 
        creationMode: string, scopeLevel: string, resourceGroupName: string, callbackInvocation: boolean,
         callbackParameters: { [key: string]: string; }, appServiceName: string): Promise<AzureAppService[]> {
        let serviceEndpointDetails = this._getServiceEndpointDetailsForAzureRM(authenticationType, creationMode, scopeLevel, resourceGroupName, azureSubscription, undefined);
        serviceEndpointDetails!.data["webApp"] = appServiceName;
        return new Promise((resolve, reject) => {
            this._getServiceEndpointRequestData(
                azureSubscription,
                undefined,
                accessToken,
                Constants.DataSourceName.AzureRMWebAppSlots,
                serviceEndpointDetails,
                "{ \"Name\" : \"{{name}}\", \"Id\" : \"{{id}}\" }",
                callbackInvocation,
                callbackParameters
            ).then((result) => {
                resolve(this._parseAppServiceSlotResultJson(result.data));
            });
        });
    }

    private _getServiceEndpointRequestData(
        azureSubscription: AzureSubscription,
        endpoint: ServiceEndpoint | undefined,
        accessToken: string,
        dataSourceName: string,
        connectionDetails: ServiceEndpointDetails | undefined,
        resultTemplate: string = "",
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): Promise<IAzureRequestResult> {

        return new Promise((resolve, reject) => {

            const fetchEndpointRequestResult = (serviceEndpointRequest: ServiceEndpointRequest, projectId: string, connectionId: string) => {
                this._getServiceEndpointsClient().executeServiceEndpointRequest(serviceEndpointRequest, projectId, connectionId).then((requestResult: ServiceEndpointRequestResult) => {
                    if (!!requestResult.errorMessage) {
                        reject(requestResult.errorMessage);
                    }
                    resolve({
                        data: requestResult.result,
                        callbackRequired: requestResult.callbackRequired,
                        callbackParameters: requestResult.callbackContextParameters
                    });
                }, (error: any) => {
                    reject(error);
                });
            };

            let serviceEndpointDetails: ServiceEndpointDetails = connectionDetails || this._getServiceEndpointDetails(azureSubscription, endpoint);
            let connectionId: string = (!!endpoint) ? endpoint.id : "0";

            let serviceEndpointRequest: ServiceEndpointRequest = {
                serviceEndpointDetails: (!!endpoint) ? null as any as ServiceEndpointDetails : serviceEndpointDetails,
                dataSourceDetails: {
                    dataSourceName: dataSourceName,
                    dataSourceUrl: "",
                    headers: [],
                    requestContent: "",
                    requestVerb: "",
                    resourceUrl: "",
                    parameters: callbackInvocation ? callbackParameters : {},
                    resultSelector: "",
                    initialContextTemplate: ""
                },
                resultTransformationDetails: {
                    resultTemplate: resultTemplate,
                    callbackContextTemplate: "",
                    callbackRequiredTemplate: ""
                }
            };

            const projectData: IProjectData | undefined = getProject(this.pageContext);

            this.getAccessToken(azureSubscription.subscriptionTenantId, accessToken, null).then((accessTokenKey: string) => {
                if (accessTokenKey) {
                    const accessTokenKeyString = (serviceEndpointRequest.serviceEndpointDetails.type === Constants.Kubernetes) ? "azureAccessToken" : "accessToken";
                    serviceEndpointRequest.serviceEndpointDetails.authorization.parameters[accessTokenKeyString] = accessTokenKey;
                    serviceEndpointRequest.serviceEndpointDetails.authorization.parameters["accessTokenFetchingMethod"] = AccessTokenRequestType.Oauth.toString();
                }

                fetchEndpointRequestResult(serviceEndpointRequest, projectData ? projectData.id : "", connectionId);
            });
        });
    }

    private _getServiceEndpointDetails(subscription?: AzureSubscription, endpoint?: ServiceEndpoint): ServiceEndpointDetails {

        let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;

        if (subscription) {
            let endpointAuthorization: EndpointAuthorization = {
                parameters: {
                    accessTokenType: Constants.AppToken,
                    tenantId: subscription.subscriptionTenantId
                },
                scheme: Constants.EndpointAuthorizationSchemes.ServicePrincipal
            };

            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    id: newGuid(),
                    name: newGuid(),
                    subscriptionId: subscription.subscriptionId,
                    subscriptionName: subscription.displayName,
                    environment: Constants.AzureCloud,
                    scopeLevel: Constants.Subscription
                },
                type: Constants.Azurerm,
                url: "https://management.azure.com/"
            };
        } else {
            serviceEndpointDetails = endpoint;
        }

        return serviceEndpointDetails!;
    }

    private _getServiceEndpointDetailsForNamespaces(
        clusterId: string,
        clusterFqdn: string,
        azureSubscription?: AzureSubscription,
        endpoint?: ServiceEndpoint
    ): ServiceEndpointDetails {

        let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;

        if (azureSubscription) {
            let endpointAuthorization: EndpointAuthorization = {
                parameters: {
                    azureEnvironment: Constants.AzureCloud,
                    azureTenantId: azureSubscription.subscriptionTenantId
                },
                scheme: Constants.EndpointAuthorizationSchemes.Kubernetes
            };

            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    authorizationType: Constants.AzureSubscription,
                    azureSubscriptionId: azureSubscription.subscriptionId,
                    azureSubscriptionName: azureSubscription.displayName,
                    clusterId: clusterId
                },
                type: Constants.Kubernetes,
                url: clusterFqdn
            };
        } else {
            serviceEndpointDetails = endpoint;
        }

        return serviceEndpointDetails!;
    }

    private _getServiceEndpointDetailsForAzureRM(
        authenticationType: string,
        creationMode: string,
        scopeLevel: string,
        resourceGroupName?: string,
        azureSubscription?: AzureSubscription,
        endpoint?: ServiceEndpoint
    ): ServiceEndpointDetails {

        let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;

        if (azureSubscription) {
            let endpointAuthorization: EndpointAuthorization = {
                parameters: {
                    accessTokenType: Constants.AppToken,
                    authenticationType: authenticationType,
                    serviceprincipalid: "",
                    serviceprincipalkey: "",
                    tenantid: azureSubscription.subscriptionTenantId
                },
                scheme: Constants.EndpointAuthorizationSchemes.ServicePrincipal
            };

            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    appObjectId: "",
                    azureSpnPermissions: "",
                    azureSpnRoleAssignmentId: "",
                    creationMode: creationMode,
                    environment: Constants.AzureCloud,
                    scopeLevel: scopeLevel,
                    spnObjectId: "",
                    subscriptionId: azureSubscription.subscriptionId,
                    subscriptionName: azureSubscription.displayName,
                },
                type: Constants.Azurerm,
                url: "https://management.azure.com/"
            };

            if (resourceGroupName) {
                serviceEndpointDetails!.data["resourceGroupName"] = resourceGroupName;
            }

        } else {
            serviceEndpointDetails = endpoint;
        }

        return serviceEndpointDetails!;
    }

    private _parseClusterResultJson(payload: any): any {
        let clusters: IKubernetesCluster[] = [];
        const clustersJson = payload as any[];
        if (clustersJson && clustersJson.length > 0) {
            clustersJson.forEach(clusterJson => {
                const cluster = safeParseJson(clusterJson);
                if (cluster) {
                    let fqdn: string = cluster.properties["fqdn"];
                    if (fqdn && !fqdn.startsWith("https://")) {
                        fqdn = "https://" + fqdn;
                    }

                    clusters.push({
                        id: cluster["id"],
                        name: cluster["name"],
                        fqdn: fqdn,
                        resourceGroup: getResourceGroupFromClusterId(cluster.id as string)
                    });
                }
            });
        }

        return clusters;
    }

    private _parseNamespacesResultJson(payload: any): any {
        let k8sNamespaces: string[] = [];
        const namespacesJson = payload as any[];
        if (namespacesJson && namespacesJson.length > 0) {
            namespacesJson.forEach(namespaceJson => {
                const k8sNamespace = safeParseJson(namespaceJson);
                if (k8sNamespace) {
                    k8sNamespaces.push(k8sNamespace.metadata["name"]);
                }
            });
        }
        return k8sNamespaces;
    }

    private _parseMLWorkspacesResultJson(payload: any): AzureMLWorkspace[] {
        let mlWorkspaces: AzureMLWorkspace[] = [];
        const mlWorkspacesJson = payload as any[];
        if (mlWorkspacesJson && mlWorkspacesJson.length > 0) {
            mlWorkspacesJson.forEach(mlWorkspaceJson => {
                const workspace = safeParseJson(mlWorkspaceJson);
                mlWorkspaces.push({
                    id: workspace["Id"],
                    name: workspace["Name"],
                    location: workspace["Location"]
                });
            });
        }

        return mlWorkspaces;
    }

    private _parseAppServiceResultJson(payload: any): AzureAppService[] {
        let appServiceList: AzureAppService[] = [];
        const appServiceListJson = payload as any[];
        if (appServiceListJson && appServiceListJson.length > 0) {
            appServiceListJson.forEach(appServiceJson => {
                const appService = safeParseJson(appServiceJson);
                appServiceList.push({
                    id: appService["Id"],
                    name: appService["Name"],
                });
            });
        }
        return appServiceList;
    }

    private _parseAppServiceSlotResultJson(payload: any): AzureAppService[] {
        let slotList: AzureAppService[] = [];
        const slotListJson = payload as any[];
        if (slotListJson && slotListJson.length > 0) {
            slotListJson.forEach(slotJson => {
                const slot = safeParseJson(slotJson);
                let slotName = (slot["Name"] as string).replace("/", "-");
                slotList.push({
                    id: slot["Id"],
                    name: slotName,
                });
            });
        }
        return slotList;
    }

    private _getTaskAgentClient(): ITaskAgentRestClient {
        return getTaskAgentClient(this.pageContext);
    }

    private _getServiceEndpointsClient(): IServiceEndpointRestClient {
        return getServiceEndpointClient(this.pageContext);
    }

    private _aadTenantId: string;
    private _accessTokenCachePromise: { [key: string]: Promise<string | undefined> } = {};
}

Services.add("IAzureResourcesService", { serviceFactory: AzureResourcesService });
